import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { ChangeEvent, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import Header from '@/component/Header';
import { z as zod } from 'zod';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { POINT_LEDGER_STATUS } from '@/lib/common/name-space';
import { CreateUserLedger } from '@/api/point/create';
import { UpdateUserTot } from '@/api/point/update';
import OtpDialog from '@/component/otp/OtpDialog';

const schema = zod.object({
  id: zod.string().min(1, { message: '필수 입력' }),
  code: zod.string().min(1, { message: '필수 입력' }),
  season: zod.number().min(1, { message: '필수 입력' }),
  amount: zod.string().min(1, { message: '필수 입력' }),
  status: zod.number().min(0, { message: '필수 입력' }),
  memo: zod.string().optional().nullable(),
});

type Values = zod.infer<typeof schema>;
const defaultValues = {
  id: '',
  code: '',
  season: 0,
  amount: '',
  status: -1,
  memo: null,
} satisfies Values;

const categories = [
  { value: POINT_LEDGER_STATUS.MINE, label: '채굴' },
  { value: POINT_LEDGER_STATUS.REFERRAL, label: '추천인' },
  { value: POINT_LEDGER_STATUS.BONUS, label: '보너스' },
  { value: POINT_LEDGER_STATUS.REFERRAL_MY, label: '피추천인' },
];

export default function AddUserPoint() {
  const [value, setValue] = useState('0');
  const [isOtpDialogOpen, setIsOtpDialogOpen] = useState(false);
  const [pendingData, setPendingData] = useState<Values | null>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value);
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  const handleOtpSuccess = async () => {
    if (!pendingData) return;

    try {
      let re = null;
      if (value === '1') {
        re = await CreateUserLedger(pendingData);
      } else if (value === '2') {
        re = await UpdateUserTot(pendingData);
      }

      if (!re || re.status !== 200) {
        toast.error('실패');
        return;
      }

      toast.success('성공');
      reset();
      setValue('0');
      setIsOtpDialogOpen(false);
      setPendingData(null);
    } catch (e) {
      console.log(e);
      toast.error('처리 중 오류가 발생했습니다.');
    }
  };

  const handleOtpClose = () => {
    setIsOtpDialogOpen(false);
    setPendingData(null);
  };

  const onSubmit = async (data: Values) => {
    // 유효성 검증
    if (value === '0') {
      toast.error('사용자 타입을 반드시 선택하세요.');
      return;
    } else if (data.season === 0) {
      toast.error('올바른 시즌이 아닙니다.');
      return;
    } else if (data.status === -1) {
      toast.error('올바른 상태를 입력하세요.');
      return;
    } else if (data.status === POINT_LEDGER_STATUS.REFERRAL && data?.memo === null) {
      toast.error('추천인 코드를 메모에 입력하세요.');
      return;
    }

    // 데이터 저장 후 OTP 다이얼로그 열기
    setPendingData(data);
    setIsOtpDialogOpen(true);
  };
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          overflow: 'auto',
          flex: 1,
          flexDirection: 'column',
          gap: { xs: '16px', sm: '0px' },
        }}
      >
        <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Header title="누락된 사용자 포인트 수동 추가" />
          <Divider />
          <CardContent
            sx={{
              // flex: 1, // CardContent가 나머지 공간을 차지하도록 설정
              overflow: 'auto', // 스크롤을 가능하게 함
            }}
          >
            <FormControl>
              <FormLabel id="group-type">사용자 타입</FormLabel>
              <RadioGroup row name="radio-button" aria-labelledby="group-type" value={value} onChange={handleChange}>
                <FormControlLabel value="0" disabled control={<Radio />} label="반드시 둘 중 하나 선택해야 함" />
                <FormControlLabel value="1" control={<Radio />} label="ledger" />
                <FormControlLabel value="2" control={<Radio />} label="total" />
              </RadioGroup>
            </FormControl>
          </CardContent>
          {value === '1' ? 1 : value === '2' ? 2 : ''}

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid md={6} xs={12}>
                <Controller
                  control={control}
                  name="id"
                  render={({ field }) => (
                    <FormControl fullWidth required>
                      <InputLabel>사용자 id</InputLabel>
                      <OutlinedInput {...field} label="id" type="text" fullWidth />
                      {errors.id && <FormHelperText>{errors.id.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid md={6} xs={12}>
                <Controller
                  control={control}
                  name="code"
                  render={({ field }) => (
                    <FormControl fullWidth required>
                      <InputLabel>code</InputLabel>
                      <OutlinedInput {...field} label="code" type="text" fullWidth />
                      {errors.code && <FormHelperText>{errors.code.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid md={6} xs={12}>
                <Controller
                  control={control}
                  name="season"
                  render={({ field }) => (
                    <FormControl error={Boolean(errors.season)} fullWidth required>
                      <InputLabel>season</InputLabel>
                      <OutlinedInput
                        {...field}
                        label="season"
                        type="number"
                        fullWidth
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                      {errors.season ? <FormHelperText>{errors.season.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid md={6} xs={12}>
                <Controller
                  control={control}
                  name="amount"
                  render={({ field }) => (
                    <FormControl error={Boolean(errors.amount)} fullWidth required>
                      <InputLabel>amount</InputLabel>
                      <OutlinedInput {...field} label="amount" type="text" fullWidth />
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid md={6} xs={12}>
                <FormControl fullWidth error={!!errors.status} margin="normal" required>
                  <InputLabel>상태</InputLabel>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} label="상태">
                        <MenuItem value={-1} disabled>
                          선택 안 함
                        </MenuItem>
                        {categories.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.status && <FormHelperText>{errors.status.message}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid xs={12}>
                {value === '1' ? (
                  <Controller
                    control={control}
                    name="memo"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.memo)} fullWidth>
                        <InputLabel>memo</InputLabel>
                        <OutlinedInput {...field} label="memo" type="text" />
                        <FormHelperText>※ 상태가 '추천인' 일 때 반드시 메모에 추천인 코드를 넣어야 함</FormHelperText>
                      </FormControl>
                    )}
                  />
                ) : (
                  <Controller
                    control={control}
                    name="memo"
                    render={({ field }) => (
                      <FormControl error={Boolean(errors.memo)} fullWidth disabled>
                        <InputLabel>memo</InputLabel>
                        <OutlinedInput {...field} label="memo" type="text" />
                        <FormHelperText>
                          ※ 상태가 '추천인' 일 때 반드시 메모에 추천인 코드를 넣어야 함, leger 상태 일 때만 사용 가능
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                )}
              </Grid>

              <Grid xs={12}>
                <Button variant="contained" type="submit" fullWidth>
                  생성
                </Button>
              </Grid>
            </Grid>
          </form>
        </Card>
      </Box>

      {/* OTP 인증 다이얼로그 */}
      <OtpDialog
        open={isOtpDialogOpen}
        onClose={handleOtpClose}
        onSuccess={handleOtpSuccess}
        title="사용자 포인트 추가 인증"
        description="사용자 포인트 추가를 위해 OTP 인증이 필요합니다."
      />
    </>
  );
}
